import React, { lazy } from "react";

import { BrowserRouter as Router, Switch, Route } from "react-router-dom";
//context providers
import { AuthProvider } from "./context/auth-context";
import { AuthCheckProvider } from './context/auth-check-context';
import { PageTrackingProvider } from "./context/page-tracking-context";
import { FirmConfigProvider } from './context/firm-config-context';
import DynamicPageMeta from './components/DynamicPageMeta';
//components
import PrivateRoute from "./components/restricted-routes/private-route";
import AdvisorRoute from "./components/restricted-routes/advisor-route";
import AuthorizedRoute from "./components/restricted-routes/authorized-route";
import { useEffect } from "react";

import NotFound from "./routes/NotFound";
import { Suspense } from "react";
import LoadingPage from "./components/loadingPage";
import { ErrorBoundary } from "react-error-boundary";
import ErrorFallbackLegacy from "./components/errorHandling/error-fallback-page";
import CustomErrorBoundaryLegacy from "./components/ErrorBoundary";
import ErrorFallbackBun from "./components/errorHandling/ErrorFallbackBun";
import CustomErrorBoundaryBun from "./components/ErrorBoundaryBun";
import { initializeGlobalErrorHandler, handleBoundaryError } from "./utils/globalErrorHandler";
import { ReactQueryDevtools } from "react-query/devtools";
import { QueryClientProvider } from "react-query";
import queryClient from "./queryClient";
//
//Google Analyctics
import ReactGA from "react-ga4";
import { analyticsEnabled, GOOGLE_ADS_ID } from "./helpers/analytics";
import { initMetaPixel, META_PIXEL_ID } from "./helpers/metaPixel";
//--------------------------------------
//constants
import { AUTHORIZATION } from "./constants";
import { BUN_PREVIEW } from "./bunPreview";
import { NavigationPrompt } from 'ui';
//react helmet
import { Helmet } from "react-helmet";

//components
import AdminRoute from "./components/restricted-routes/admin-route";
import SuperAdminRoute from "./components/restricted-routes/super-admin-route";
import ManagerRoute from "./components/restricted-routes/manager-route";
import CustomDragLayer from "./components/drag-drop/CustomDragLayer";
import AppProvider from "./context/app-context.js";
import AutomationEventListener from "./components/AutomationEventListener";
// V2 WebSocket-based context with Claude Agent SDK
import { ParabotProviderV2 as ParabotProvider } from "./components/ai/Parabot-Interface/context/ParabotContextV2";
import { DraftProvider } from "./routes/AdvisorPortal/emails/context/DraftContext";

import { advisorHouseholdPaths, householdPages } from "./routes/Client-Portal/shared/householdPages";

// Recovery must render synchronously even when a lazy route or provider fails.
const ErrorFallback = BUN_PREVIEW ? ErrorFallbackBun : ErrorFallbackLegacy;
const CustomErrorBoundary = BUN_PREVIEW ? CustomErrorBoundaryBun : CustomErrorBoundaryLegacy;

//routes
const Documents = BUN_PREVIEW ? householdPages.vault.Page : lazy(() => import('./routes/Client-Portal/Documents'));
const SecurityPage = lazy(() => import("./routes/publicPages/Security"));
const McpAuthorize = lazy(() => import("./routes/McpAuthorize"));
const StripeTransition = lazy(() =>
  BUN_PREVIEW
    ? import("./routes/Client-Portal/stripe-transitionBun")
    : import("./routes/Client-Portal/stripe-transition")
);
//routes
const onboarding = lazy(() => BUN_PREVIEW ? import("./routes/publicPages/onboardingBun/onboarding-hub") : import("./routes/publicPages/onboarding/onboarding-hub"))
const LearningLabDashboard = lazy(() =>
  BUN_PREVIEW
    ? import("./routes/Client-Portal/LearningLabBun")
    : import("./routes/Client-Portal/LearningLab")
);
const GetStarted = lazy(() => import("./routes/publicPages/get-started"))
const SchedulePage = lazy(() => import("./routes/publicPages/schedule"))
const JoinPage = lazy(() => import("./routes/publicPages/join"))
const DiscoveryScheduled = lazy(() => import("./routes/publicPages/discovery-scheduled"))
const PrivacyPolicy = lazy(() => import("./routes/disclosures/privacy-policy"));
const NoticeAndDisclamer = lazy(() =>
  import("./routes/disclosures/notice-and-disclaimer")
);
const TermsOfUse = lazy(() => import("./routes/disclosures/terms-of-use"));
const AbundoLove = lazy(() =>
  BUN_PREVIEW
    ? import("./routes/Client-Portal/Abundo-LoveBun")
    : import("./routes/Client-Portal/Abundo-Love")
);
const Partners = lazy(() =>
  BUN_PREVIEW
    ? import("./routes/Client-Portal/PartnersBun")
    : import("./routes/Client-Portal/Partners")
);
const Resources = lazy(() => import("./routes/Client-Portal/Resources"));

const LearningLabVideo = lazy(() =>
  BUN_PREVIEW
    ? import("./routes/Client-Portal/LearningLabBun/LearningLabVideo")
    : import("./routes/Client-Portal/LearningLab/LearningLabVideo")
);
const LearningLabTopic = lazy(() =>
  BUN_PREVIEW
    ? import("./routes/Client-Portal/LearningLabBun/LearningLabTopic")
    : import("./routes/Client-Portal/LearningLab/LearningLabTopic")
);


const NetWorth = BUN_PREVIEW ? householdPages["net-worth"].Page : lazy(() => import("./routes/Client-Portal/NetWorth"));
const Spending = BUN_PREVIEW ? householdPages.spending.Page : lazy(() => import("./routes/Client-Portal/Budget/Spending"));
const Transactions = lazy(() =>
  BUN_PREVIEW
    ? import("./routes/Client-Portal/Budget/TransactionsBun")
    : import("./routes/Client-Portal/Budget/Transactions")
);
const CategoryDetail = lazy(() =>
  BUN_PREVIEW
    ? import("./routes/Client-Portal/Budget/CategoryDetailBun")
    : import("./routes/Client-Portal/Budget/CategoryDetail")
);
const BudgetEdit = lazy(() =>
  BUN_PREVIEW
    ? import("./routes/Client-Portal/Budget/BudgetEditBun")
    : import("./routes/Client-Portal/Budget/BudgetEdit")
);

const YouvestHome = lazy(() => BUN_PREVIEW ? import("./routes/Client-Portal/YouvestBun/Client") : import("./routes/Client-Portal/Youvest"));
const AdvisorYouvestHome = BUN_PREVIEW ? householdPages.youvest.Page : lazy(() => import("./routes/Client-Portal/Youvest"));
const YouvestActivityPage = lazy(() => import("./routes/Client-Portal/Youvest/Activity"));
const YouvestCoverPage = lazy(() => import("./routes/Client-Portal/Youvest/CoverPage"));
const YouvestTutorialsPage = lazy(() => import("./routes/Client-Portal/Youvest/Tutorials"));
const ClientYouvestActivityPage = lazy(() => BUN_PREVIEW ? import("./routes/Client-Portal/YouvestBun/ClientActivity") : import("./routes/Client-Portal/Youvest/Activity"));
const ClientYouvestCoverPage = lazy(() => BUN_PREVIEW ? import("./routes/Client-Portal/YouvestBun/ClientCoverPage") : import("./routes/Client-Portal/Youvest/CoverPage"));
const ClientYouvestTutorialsPage = lazy(() => BUN_PREVIEW ? import("./routes/Client-Portal/YouvestBun/ClientTutorials") : import("./routes/Client-Portal/Youvest/Tutorials"));

const financialStory = BUN_PREVIEW ? householdPages["financial-story"].Page : lazy(() => import("./routes/Client-Portal/FinancialStory"));
const Reports = lazy(() => BUN_PREVIEW ? import("./routes/Client-Portal/ReportsBun") : import("./routes/Client-Portal/Reports"));
const DesignSystemGallery = lazy(() => import("./ui/gallery/DesignSystemGallery"));
const PlanningPage = BUN_PREVIEW ? householdPages.planning.Page : lazy(() => import("./routes/Client-Portal/Planning"));
// Advisor smart-table routes — lazy so their large component graph (react-window,
// row components, modals, CSV export) stays out of the main bundle that every
// user (including clients who never see a table) downloads before first paint.
const SmartTablesHome = lazy(() => import("./routes/AdvisorPortal/SmartTablesBun"));
const HouseholdsSmartTable = lazy(() => BUN_PREVIEW ? import("./routes/AdvisorPortal/SmartTablesBun/HouseholdsSmartTable") : import("./routes/AdvisorPortal/clients/HouseholdsSmartTable"));
const UsersSmartTable = lazy(() => BUN_PREVIEW ? import("./routes/AdvisorPortal/SmartTablesBun/UsersSmartTable") : import("./routes/AdvisorPortal/clients/UsersSmartTable"));
const YouvestPortfolioSmartTable = lazy(() => import("./routes/AdvisorPortal/YouvestPortfolio"));
const ClipboardPage = BUN_PREVIEW ? householdPages.clipboard.Page : lazy(() => import("./routes/Client-Portal/Clipboard"));
const ClientOnboarding = lazy(() => BUN_PREVIEW
  ? import("./routes/Client-Portal/OnboardingBun")
  : import("./routes/Client-Portal/Onboarding"))
const ClientWelcome = lazy(() => import("./routes/Client-Portal/Onboarding/WelcomeFlowGate").then(({ default: Gate }) => ({
  default: () => <Gate><ClientOnboarding /></Gate>,
})))

const AdminDashboard = lazy(() =>
  import("./routes/admin-portal/admin-dashboard")
);
const SuperAdminPortal = lazy(() =>
  BUN_PREVIEW
    ? import("./components/AdminBun/SuperAdminPortal")
    : import("./components/Admin/SuperAdminPortal")
);
const DevDashboard = lazy(() =>
  import("./components/DevTeam/DevDashboard")
);
const ReliabilityDashboard = lazy(() =>
  import("./components/SuperAdmin/ReliabilityDashboard")
);
const SecurityCenter = lazy(() =>
  import("./components/SuperAdmin/SecurityCenterBun")
);
const DevToolsIndex = lazy(() =>
  BUN_PREVIEW
    ? import("./components/SuperAdmin/DevToolsBun/DevToolsIndex")
    : import("./components/SuperAdmin/DevTools/DevToolsIndex")
);
const PlaidInspector = lazy(() =>
  BUN_PREVIEW
    ? import("./components/SuperAdmin/DevToolsBun/PlaidInspector")
    : import("./components/SuperAdmin/DevTools/PlaidInspector")
);
const ActivityDashboard = lazy(() =>
  BUN_PREVIEW ? import("./components/DevTeam/ActivityDashboardBun") : import("./components/DevTeam/ActivityDashboard")
);
const UserTimelinePage = lazy(() =>
  BUN_PREVIEW ? import("./components/DevTeam/ActivityDashboardBun/UserTimelinePage") : import("./components/DevTeam/ActivityDashboard/UserTimelinePage")
);
const UserFlowPage = lazy(() =>
  BUN_PREVIEW ? import("./components/DevTeam/ActivityDashboardBun/UserFlowPage") : import("./components/DevTeam/ActivityDashboard/UserFlowPage")
);
const RetentionPage = lazy(() =>
  BUN_PREVIEW ? import("./components/DevTeam/ActivityDashboardBun/RetentionPage") : import("./components/DevTeam/ActivityDashboard/RetentionPage")
);
const ParabotTraces = lazy(() =>
  BUN_PREVIEW
    ? import("./components/SuperAdmin/ParabotTracesBun")
    : import("./components/SuperAdmin/ParabotTraces")
);
const ParabotFeedback = lazy(() =>
  BUN_PREVIEW
    ? import("./components/SuperAdmin/ParabotFeedbackBun")
    : import("./components/SuperAdmin/ParabotFeedback")
);
const ParabotTraceDetail = lazy(() =>
  BUN_PREVIEW
    ? import("./components/SuperAdmin/ParabotTracesBun/TraceDetail")
    : import("./components/SuperAdmin/ParabotTraces/TraceDetail")
);
const DevTicketsSmartTable = lazy(() =>
  BUN_PREVIEW
    ? import("./routes/AdvisorPortal/SmartTablesBun/DevTicketsSmartTable")
    : import("./routes/AdvisorPortal/DevTickets/DevTicketsSmartTable")
);
const PentesterAttemptsSmartTable = lazy(() =>
  import("./routes/AdvisorPortal/PentesterAttempts/PentesterAttemptsSmartTable")
);

const CustomFormsSmartTable = lazy(() => BUN_PREVIEW ? import("./routes/AdvisorPortal/SmartTablesBun/CustomFormsSmartTable") : import("./routes/AdvisorPortal/CustomForms/SmartTable"));
const ViewFormAnswers = lazy(() =>
  BUN_PREVIEW
    ? import("./routes/AdvisorPortal/CustomForms/ViewFormAnswersBun")
    : import("./routes/AdvisorPortal/CustomForms/ViewFormAnswers")
);

const AdvisorDashboard = lazy(() =>
  BUN_PREVIEW
    ? import("./routes/AdvisorPortal/DashboardBun")
    : import("./routes/AdvisorPortal/Dashboard")
);
const ClientJourney = lazy(() =>
  BUN_PREVIEW
    ? import("./routes/AdvisorPortal/clientJourneyBun/ClientJourney")
    : import("./routes/AdvisorPortal/clientJourney/ClientJourney")
);
const RemindersDashboard = lazy(() => import("./routes/AdvisorPortal/reminders/RemindersDashboard"));
const NotificationsSmartTable = lazy(() => BUN_PREVIEW ? import("./routes/AdvisorPortal/SmartTablesBun/NotificationsSmartTable") : import("./routes/AdvisorPortal/Notifications/Notifications-Smart-Table"));
const OnboardingDashboard = lazy(() => BUN_PREVIEW ? import("./routes/AdvisorPortal/SmartTablesBun/OnboardingDashboard") : import("./routes/AdvisorPortal/onboarding/OnboardingDashboard"));
const SupportSmartTable = lazy(() => BUN_PREVIEW ? import("./routes/AdvisorPortal/SmartTablesBun/SupportSmartTable") : import("./routes/AdvisorPortal/Support/SupportSmartTable"));
const FirmTasksSmartTable = lazy(() => BUN_PREVIEW ? import("./routes/AdvisorPortal/SmartTablesBun/FirmTasksSmartTable") : import("./routes/AdvisorPortal/FirmTasks/FirmTasksSmartTable"));
const FinancialStoryTasksSmartTable = lazy(() => BUN_PREVIEW ? import("./routes/AdvisorPortal/SmartTablesBun/FinancialStoryTasksSmartTable") : import("./routes/AdvisorPortal/FinancialStoryTasks/FinancialStoryTasksSmartTable"));
const ProspectDashboard = lazy(() => BUN_PREVIEW ? import("./routes/AdvisorPortal/SmartTablesBun/ProspectBoard") : import("./routes/AdvisorPortal/prospects/ProspectBoard"));
const MeetingDashboard = lazy(() => BUN_PREVIEW ? import("./routes/AdvisorPortal/SmartTablesBun/MeetingDashboard") : import("./routes/AdvisorPortal/meetings/MeetingDashboard"));
const HouseholdEmailsPage = lazy(() =>
  BUN_PREVIEW
    ? import("./routes/Client-Portal/Advisor/HouseholdEmailsPageBun")
    : import("./routes/Client-Portal/Advisor/HouseholdEmailsPage")
);

const EditContent = lazy(() =>
  BUN_PREVIEW
    ? import("./routes/Content/learningLabBun/edit-content")
    : import("./routes/Content/learningLab/edit-content")
);
const EditLearningLabDashboard = lazy(() =>
  BUN_PREVIEW
    ? import("./routes/Content/learningLabBun/edit-learning-lab-dashboard")
    : import("./routes/Content/learningLab/edit-learning-lab-dashboard")
);
const EditBlog = lazy(() => BUN_PREVIEW
  ? import("./routes/Content/blogBun/edit-blog")
  : import("./routes/Content/blog/edit-blog"));
const BlogDashboard = lazy(() =>
  BUN_PREVIEW
    ? import("./routes/Content/blogBun/blog-dashboard")
    : import("./routes/Content/blog/blog-dashboard")
);




const ClientDashboard = lazy(() => BUN_PREVIEW
  ? import("./routes/Client-Portal/Client-DashboardBun")
  : import("./routes/Client-Portal/Client-Dashboard"));
const ClientSmartTables = lazy(() => import("./routes/Client-Portal/SmartTables"));
// Bun redesign rollout gate (docs/BUN_REDESIGN_PLAN.md): BUN_PREVIEW is a
// runtime flag that defaults to Bun for clients and advisors. ?bun=off selects
// legacy and ?bun=on restores Bun (sticky per browser; see src/bunPreview.js).
// The unloaded side of each seam stays out of the served bundle.
const Login = lazy(() =>
  BUN_PREVIEW ? import("./routes/Login/bun/Login") : import("./routes/Login/Login")
);
const PubHomePage = lazy(() =>
  import("./routes/publicPages/home-page")
);
const Settings = lazy(() =>
  BUN_PREVIEW
    ? import('./routes/Client-Portal/SettingsBun')
    : import('./routes/Client-Portal/Settings')
);
const AdvisorPersonalSettings = lazy(() => import('./routes/AdvisorPortal/PersonalSettingsBun'));
const whatToExpect = lazy(() => import("./routes/publicPages/what-to-expect"));
const Signup = lazy(() =>
  BUN_PREVIEW ? import("./routes/Login/bun/Signup") : import("./routes/Login/Signup")
);
const ForgotPassword = lazy(() =>
  BUN_PREVIEW ? import("./routes/Login/bun/ForgotPassword") : import("./routes/Login/forgot-password")
);
const Careers = lazy(() => import("./routes/publicPages/careers"));
const JustGettingStarted = lazy(() => import("./routes/publicPages/who-we-help/just-getting-started"));
const ConsideringRetirement = lazy(() => import("./routes/publicPages/who-we-help/considering-retirement"));
const MidCareerProfessionals = lazy(() => import("./routes/publicPages/who-we-help/mid-career-professionals"));
const DIYer = lazy(() => import("./routes/publicPages/who-we-help/diyer"));
const NewAdvisor = lazy(() => import("./routes/publicPages/who-we-help/new-advisor"));
const FeeSavingsCalculator = lazy(() => import("./routes/publicPages/fee-savings-calculator"));
const OurStory = lazy(() => import("./routes/publicPages/OurStory"));

const Faq = lazy(() => import("./routes/publicPages/Faq"));
const Media = lazy(() => import("./routes/publicPages/media"));
const BlogHome = lazy(() => import("./routes/publicPages/Blog/blog-home"));
const BlogPost = lazy(() => import("./routes/publicPages/Blog/blog-post"));
const BlogCateogry = lazy(() => import("./routes/publicPages/Blog/Blog-Category"));
const UnclaimedMeetings = lazy(() => import('./routes/AdvisorPortal/meetings/components/unclaimedMeetings/UnclaimedTable'))
const Values = lazy(() => BUN_PREVIEW
  ? import('./routes/Client-Portal/ValuesBun')
  : import('./routes/Client-Portal/Values'));
const RiskToleranceForm = lazy(() => BUN_PREVIEW
  ? import('./routes/Client-Portal/Risk-ToleranceBun/Form')
  : import('./routes/Client-Portal/Risk-Tolerance/Form'))
const YouvestRiskToleranceForm = lazy(() => BUN_PREVIEW
  ? import('./routes/Client-Portal/YouvestBun/RiskToleranceForm.jsx')
  : import('./routes/Client-Portal/Youvest/RiskToleranceForm.jsx'))
const EndPageRiskTolerance = lazy(() => BUN_PREVIEW
  ? import('./routes/Client-Portal/Risk-ToleranceBun/EndPage')
  : import('./routes/Client-Portal/Risk-Tolerance/EndPage'))

const AdvisorResources = lazy(() =>
  BUN_PREVIEW
    ? import("./routes/AdvisorPortal/Advisor-ResourcesBun")
    : import("./routes/AdvisorPortal/Advisor-Resources")
)
const AdvisorResourcePage = lazy(() =>
  BUN_PREVIEW
    ? import("./routes/AdvisorPortal/Advisor-ResourcesBun/components/AdvisorResourcePage")
    : import("./routes/AdvisorPortal/Advisor-Resources/components/AdvisorResourcePage")
)
const AdviceLibraryManagement = lazy(() =>
  BUN_PREVIEW
    ? import("./routes/AdvisorPortal/Advice-LibraryBun")
    : import("./routes/AdvisorPortal/Advice-Library")
)
const AdviceLibraryTopicDetail = lazy(() =>
  BUN_PREVIEW
    ? import("./routes/AdvisorPortal/Advice-LibraryBun/TopicDetailPage")
    : import("./routes/AdvisorPortal/Advice-Library/TopicDetailPage")
)
const ClientAdviceLibrary = lazy(() =>
  BUN_PREVIEW
    ? import("./routes/Client-Portal/Advice-LibraryBun")
    : import("./routes/Client-Portal/Advice-Library")
)
const SubAgentsManagement = lazy(() =>
  import("./routes/AdvisorPortal/SubAgents")
)
const AdvisorHouseholdDashboard = BUN_PREVIEW ? householdPages.overview.Page : lazy(() => import("./routes/Client-Portal/Advisor"));
const HouseholdOnePager = householdPages['one-pager'].Page;

const AnalyticsModule = lazy(() => BUN_PREVIEW
  ? import('./routes/AdvisorPortal/AnalyticsBun')
  : import('./routes/AdvisorPortal/Analytics'));

const AdvisorSettings = lazy(() =>
  BUN_PREVIEW
    ? import('./routes/AdvisorPortal/SettingsBun')
    : import('./routes/AdvisorPortal/Settings')
);
const ManagerDashboard = lazy(() => BUN_PREVIEW
  ? import('./routes/manager-portal/manager-dashboardBun')
  : import('./routes/manager-portal/manager-dashboard'));
const ParabotUsage = lazy(() => BUN_PREVIEW
  ? import('./routes/manager-portal/Parabot-ussageBun')
  : import('./routes/manager-portal/Parabot-ussage'));
const AdvisorActivityPage = lazy(() => BUN_PREVIEW
  ? import('./routes/manager-portal/advisor-activityBun')
  : import('./routes/manager-portal/advisor-activity'));
const NotesPage = lazy(() => BUN_PREVIEW ? import("./routes/Client-Portal/NotesBun") : import("./routes/Client-Portal/Notes"));
const AdvisorNotesPage = BUN_PREVIEW ? householdPages.notes.Page : lazy(() => import('./routes/Client-Portal/Notes'));
const Documentation = lazy(() => import("./routes/Documentation"));

//--------------------------
const UUID_ROUTE_SEGMENT = "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}";

// The shared QueryClient lives in ./queryClient so non-hook code (e.g. the
// Planning module's imperative fetches) can use the same cache instance.

const App = () => {


  // Defer GA4 initialization until after first render
  useEffect(() => {
    if (analyticsEnabled()) {
      const TRACKING_ID = "G-SGWZ3638X5";
      // Register GA4 and the Google Ads tag on the same gtag.js instance.
      ReactGA.initialize([
        { trackingId: TRACKING_ID },
        { trackingId: GOOGLE_ADS_ID },
      ]);
      const pathName = window.location.pathname + window.location.search;
      ReactGA.send({ hitType: "pageview", page: pathName });
    }
  }, []);

  // Defer Meta Pixel until after first paint (was an inline <script> in
  // public/index.html; moved to a module so the SPA HTML can ship CSP-clean).
  useEffect(() => {
    if (analyticsEnabled()) {
      const start = () => initMetaPixel(META_PIXEL_ID);
      if (typeof window !== "undefined" && "requestIdleCallback" in window) {
        window.requestIdleCallback(start);
      } else {
        setTimeout(start, 2500);
      }
    }
  }, []);

  // Initialize global error handler
  useEffect(() => {
    initializeGlobalErrorHandler();
  }, []);

  //react query
  return (
    <div className="App">
      <Helmet>
        <meta
          name="google-site-verification"
          content="0OLDiDXXqGQ_wBXoQUjSezaQseCmWhSIZEs9oYlO_f4"
        />

        <meta
          name="keywords"
          content="Financial advisor, Financial planning, Financial advisor,Advice-Only financial advisor, Advice-Only, Advice-Only financial advice, Flat fee financial advisor,Flat fee financial advice,Best financial advisor,Advice-only financial advisor,Best financial advice,Low cost financial advice,Comprehensive, Transparent, Travel hacks, Credit card points, Student loans, buying a home, buy a home, home buying, FI-RE, Financial independence, retire early, cost of financial advice, virtual financial advice, virtual financial planner, virtual financial planning, financial literacy, financial freedom, investing, digital nomads, health care workers, teachers, travel lovers"
        />
        {process.env.NODE_ENV !== "production" && <script
          crossOrigin="anonymous"
          src="//unpkg.com/react-scan/dist/auto.global.js"
        ></script>}
      </Helmet>
      <CustomErrorBoundary>
        <ErrorBoundary FallbackComponent={ErrorFallback} onError={handleBoundaryError}>
          <QueryClientProvider client={queryClient}>
            <FirmConfigProvider>
            <DynamicPageMeta />
            <AuthProvider>
              <AutomationEventListener />
              <AppProvider>
                <ParabotProvider>
                  <AuthCheckProvider>
                    <DraftProvider>
                    <div className="bg-gray-100 lg:bg-gray-100 h-0">
                      <NavigationPrompt.Provider>{getUserConfirmation => <Router getUserConfirmation={getUserConfirmation}>
                      <PageTrackingProvider>
                      <Suspense fallback={<LoadingPage />}>
                        {" "}
                        <CustomDragLayer />
                        <Switch>
                          <Route exact path="/" component={PubHomePage} />
                          <Route exact path="/what-to-expect" component={whatToExpect} />

                          <Route exact path="/login" component={Login} />
                          <Route exact path="/signup" component={Signup} />
                          <Route exact path="/connect-mcp" component={McpAuthorize} />
                          <Route
                            exact
                            path="/forgotpassword"
                            component={ForgotPassword}
                          />
                          <Route exact path="/careers" component={Careers} />
                          <Route exact path="/security" component={SecurityPage} />
                          <Route exact path="/media" component={Media} />
                          <Route exact path="/privacy-policy" component={PrivacyPolicy} />
                          <Route exact path="/who-we-help/just-getting-started" component={JustGettingStarted} />
                          <Route exact path="/who-we-help/thinking-about-retirement" component={ConsideringRetirement} />
                          <Route exact path="/who-we-help/mid-career-professionals" component={MidCareerProfessionals} />
                          <Route exact path="/who-we-help/diyer-seeking-guidance" component={DIYer} />
                          <Route exact path="/who-we-help/looking-for-advisor" component={NewAdvisor} />
                          <Route exact path="/our-story" component={OurStory} />
                          <Route exact path='/about' component={OurStory} />
                          <Route exact path="/faq" component={Faq} />
                          <Route exact path='/fee-savings-calculator' component={FeeSavingsCalculator} />
                          <Route exact path="/blog" component={BlogHome} />{" "}
                          <Route exact path='/blog/categories/:category_href' component={BlogCateogry} />
                          <Route exact path="/blog/:blog_href" component={BlogPost} />
                          <Route exact path="/get-started" component={GetStarted} />
                          <Route exact path="/schedule" component={SchedulePage} />
                          <Route exact path="/join" component={JoinPage} />
                          <Route exact path="/discovery-scheduled" component={DiscoveryScheduled} />
                          <Route exact path="/vault" component={Documents} />
                          {/* Onboarding */}
                          <Route
                            exact
                            path="/onboarding/:id"
                            component={onboarding}
                          />
                          <PrivateRoute exact path="/settings" component={Settings} />
                          <PrivateRoute
                            exact
                            path="/stripe-transition/:id"
                            component={StripeTransition}
                          />
                          <PrivateRoute exact path="/net-worth/:tab?" component={NetWorth} />
                          <PrivateRoute exact path="/spending/:tab?" component={Spending} />
                          <PrivateRoute exact path="/transactions" component={Transactions} />
                          <PrivateRoute exact path="/spending/category/:categoryName" component={CategoryDetail} />
                          <PrivateRoute exact path="/budgets" component={BudgetEdit} />

                          <PrivateRoute
                            exact
                            path="/dashboard"
                            component={ClientDashboard}
                          />
                          <PrivateRoute exact path="/reports" component={Reports} />
                          {/* Legal ------------------------------------------------------------------- Legal */}
                          <Route
                            exact
                            path="/legal/terms_of_use"
                            component={TermsOfUse}
                          />
                          <Route
                            exact
                            path="/legal/notice_and_disclamer"
                            component={NoticeAndDisclamer}
                          />
                          <Route
                            exact
                            path="/legal/privacy_policy"
                            component={PrivacyPolicy}
                          />
                          <PrivateRoute
                            exact
                            path='/risk-tolerance/form'
                            component={RiskToleranceForm}
                          />
                          <PrivateRoute
                            exact
                            path='/risk-tolerance/youvest-form'
                            component={YouvestRiskToleranceForm}
                          />
                          <PrivateRoute
                            exact
                            path='/risk-tolerance/end-page'
                            component={EndPageRiskTolerance}
                          />
                          {/*Learning Lab ------------------------------------------------------- Learning Lab */}
                          <PrivateRoute
                            exact
                            path="/learning_lab"
                            component={LearningLabDashboard}
                          />
                          <PrivateRoute exact path='/partners' component={Partners} />
                          <PrivateRoute exact path='/resources' component={Resources} />

                          <PrivateRoute
                            exact
                            path="/learning_lab/:section"
                            component={LearningLabTopic}
                          />
                          <PrivateRoute
                            exact
                            path="/learning_lab/:section/:id"
                            component={LearningLabVideo}
                          />{" "}
                          {/* ----------------------------------------- Travel Lounge */}

                          {/* ---------------------------------------------- Values Exercise */}
                          <PrivateRoute
                            exact
                            path="/values/exercise"
                            component={Values}
                          />
                          {/* ------------------------------------------ End Values Exercise */}
                          {/* ------------------------------------------ Priorities */}
                          <PrivateRoute
                            exact
                            path="/financial-story"
                            component={financialStory}
                          />
                          <PrivateRoute
                            exact
                            path="/financial-story/:chapterId"
                            component={financialStory}
                          />
                          {/* ------------------------------------------ End Priorities */}
                          {/* ------------------------------------------  Investing Hub*/}

                          
                          <PrivateRoute
                            exact
                            path="/youvest/cover-page"
                            component={ClientYouvestCoverPage}
                          />
                          <PrivateRoute
                            exact
                            path="/youvest/activity"
                            component={ClientYouvestActivityPage}
                          />
                          <PrivateRoute
                            exact
                            path="/youvest/tutorials/:custodian"
                            component={ClientYouvestTutorialsPage}
                          />
                          <PrivateRoute
                            exact
                            path="/youvest/tutorials"
                            component={ClientYouvestTutorialsPage}
                          />

                          <PrivateRoute
                            exact
                            path="/youvest"
                            component={YouvestHome}
                          />
                      
                          <AdvisorRoute
                            exact
                            path='/crm/households/:household_id/youvest/cover-page'
                            component={BUN_PREVIEW ? AdvisorYouvestHome : YouvestCoverPage}
                          />
                          <AdvisorRoute
                            exact
                            path='/crm/households/:household_id/youvest/activity'
                            component={BUN_PREVIEW ? AdvisorYouvestHome : YouvestActivityPage}
                          />
                          <AdvisorRoute
                            exact
                            path='/crm/households/:household_id/youvest/tutorials/:custodian'
                            component={BUN_PREVIEW ? AdvisorYouvestHome : YouvestTutorialsPage}
                          />
                          <AdvisorRoute
                            exact
                            path='/crm/households/:household_id/youvest/tutorials'
                            component={BUN_PREVIEW ? AdvisorYouvestHome : YouvestTutorialsPage}
                          />

                          <AdvisorRoute
                            exact
                            path='/crm/households/:household_id/youvest'
                            component={AdvisorYouvestHome}
                          />
                          {/* Advisor Household-Specific Routes */}
                          <AdvisorRoute
                            exact
                            path="/crm/households/:household_id/dashboard"
                            component={ClientDashboard}
                          />
                          <AdvisorRoute exact path="/crm/households/:household_id/net-worth/:tab?" component={NetWorth} />
                          <AdvisorRoute exact path="/crm/households/:household_id/spending/:tab?" component={Spending} />
                          <AdvisorRoute exact path="/crm/households/:household_id/transactions" component={Transactions} />
                          <AdvisorRoute exact path="/crm/households/:household_id/spending/category/:categoryName" component={CategoryDetail} />
                          <AdvisorRoute exact path="/crm/households/:household_id/budgets" component={BudgetEdit} />
                          <AdvisorRoute
                            exact
                            path="/crm/households/:household_id/vault"
                            component={Documents}
                          />
                          <AdvisorRoute
                            exact
                            path="/crm/households/:household_id/reports"
                            component={Reports}
                          />
                          <AdvisorRoute
                            exact
                            path="/crm/households/:household_id/financial-story"
                            component={financialStory}
                          />
                          <AdvisorRoute
                            exact
                            path="/crm/households/:household_id/smart-tables"
                            component={ClientSmartTables}
                          />
                          <AdvisorRoute
                            exact
                            path="/crm/households/:household_id/financial-story/:chapterId"
                            component={financialStory}
                          />
                          <AdvisorRoute
                            exact
                            path="/crm/households/:household_id/onboarding/:step?"
                            component={ClientOnboarding}
                          />
                          <AdvisorRoute
                            exact
                            path="/crm/households/:household_id/planning/:toolId?"
                            component={PlanningPage}
                          />
                          <AdvisorRoute
                            exact
                            path="/crm/households/:household_id/clipboard"
                            component={ClipboardPage}
                          />
                          <AdvisorRoute
                            exact
                            path="/crm/households/:household_id/learning_lab"
                            component={LearningLabDashboard}
                          />
                          <AdvisorRoute
                            exact
                            path="/crm/households/:household_id/learning_lab/:section"
                            component={LearningLabTopic}
                          />
                          <AdvisorRoute
                            exact
                            path="/crm/households/:household_id/learning_lab/:section/:id"
                            component={LearningLabVideo}
                          />
                          <AdvisorRoute
                            exact
                            path="/crm/households/:household_id/partners"
                            component={Partners}
                          />
                          <AdvisorRoute
                            exact
                            path="/crm/households/:household_id/resources"
                            component={Resources}
                          />
                          <AdvisorRoute
                            exact
                            path="/crm/households/:household_id/documentation"
                            component={Documentation}
                          />
                          <AdvisorRoute
                            exact
                            path="/crm/households/:household_id/documentation/:sectionId"
                            component={Documentation}
                          />
                          <AdvisorRoute
                            exact
                            path="/crm/households/:household_id/settings"
                            component={Settings}
                          />
                          <AdvisorRoute
                            exact
                            path="/crm/households/:household_id/abundo-love-corner"
                            component={AbundoLove}
                          />
                          <AdvisorRoute
                            exact
                            path={advisorHouseholdPaths}
                            component={AdvisorHouseholdDashboard}
                          />
                          <AdvisorRoute
                            exact
                            path="/crm/households/:household_id/one-pager"
                            component={HouseholdOnePager}
                          />
                        
                          <AdvisorRoute
                            exact
                            path="/crm/households/:household_id/risk-tolerance/form"
                            component={RiskToleranceForm}
                          />
                          <AdvisorRoute
                            exact
                            path="/crm/households/:household_id/risk-tolerance/youvest-form"
                            component={YouvestRiskToleranceForm}
                          />
                          <AdvisorRoute
                            exact
                            path="/crm/households/:household_id/risk-tolerance/end-page"
                            component={EndPageRiskTolerance}
                          />
                          <AdvisorRoute
                            exact
                            path="/crm/households/:household_id/values/exercise"
                            component={Values}
                          />
                          <AdvisorRoute
                            exact
                            path="/crm/households/:household_id/notes"
                            component={AdvisorNotesPage}
                          />
                          <AdvisorRoute
                            exact
                            path="/crm/households/:household_id/emails"
                            component={HouseholdEmailsPage}
                          />
                          <AdvisorRoute
                            exact
                            path="/crm/households/:household_id/advice-library"
                            component={ClientAdviceLibrary}
                          />
                          <AdvisorRoute
                            exact
                            path="/crm/households/:household_id/advice-library/:topicId"
                            component={ClientAdviceLibrary}
                          />


                          <PrivateRoute
                            exact
                            path="/abundo-love-corner"
                            component={AbundoLove}
                          />

                          <PrivateRoute
                            exact
                            path="/notes"
                            component={NotesPage}
                          />

                          <PrivateRoute
                            exact
                            path="/welcome/:step?"
                            component={ClientWelcome}
                          />
                          <AdvisorRoute
                            exact
                            path="/planning/:toolId?"
                            component={PlanningPage}
                          />
                          <AdvisorRoute
                            exact
                            path="/clipboard"
                            component={ClipboardPage}
                          />

                          <PrivateRoute
                            exact
                            path="/documentation"
                            component={Documentation}
                          />
                          <PrivateRoute
                            exact
                            path="/documentation/:sectionId"
                            component={Documentation}
                          />


                          {/* Manager ------------------------------------------------------------------- Manager */}
                          <ManagerRoute
                            exact
                            path="/crm/manager/dashboard"
                            component={ManagerDashboard}
                          />
                          <ManagerRoute
                            exact
                            path="/crm/manager/parabot-usage"
                            component={ParabotUsage}
                          />
                          <ManagerRoute
                            exact
                            path="/crm/manager/advisor-activity"
                            component={AdvisorActivityPage}
                          />
                          {/* Advisor ------------------------------------------------------------------- Advisor */}{" "}
                          <AdvisorRoute
                            exact
                            path="/crm/design-system"
                            component={DesignSystemGallery}
                          />
                          <AdvisorRoute
                            exact
                            path="/crm/smart-tables"
                            component={SmartTablesHome}
                          />
                          <AdvisorRoute
                            exact
                            path="/crm/households/smart-table/:view_id"
                            component={HouseholdsSmartTable}
                          />

                          <AdvisorRoute
                            exact
                            path="/crm/support/smart-table/:view_id"
                            component={SupportSmartTable}
                          />

                          <AdvisorRoute
                            exact
                            path="/crm/youvest-portfolio/smart-table/:view_id"
                            component={YouvestPortfolioSmartTable}
                          />
                          <AdvisorRoute
                            exact
                            path="/crm/firm-tasks/smart-table/:view_id"
                            component={FirmTasksSmartTable}
                          />
                          <AdvisorRoute
                            exact
                            path="/crm/financial-story-tasks/smart-table/:view_id"
                            component={FinancialStoryTasksSmartTable}
                          />

                          <AdvisorRoute
                            exact
                            path="/crm/advisor/settings/:user_id"
                            component={AdvisorSettings}
                          />
                          {BUN_PREVIEW && <AdvisorRoute
                            exact
                            path="/crm/personal-settings"
                            component={AdvisorPersonalSettings}
                          />}
                          <AdvisorRoute
                            exact
                            path="/crm/users/smart-table/:view_id"
                            component={UsersSmartTable}
                          />
                          <AdvisorRoute
                            exact
                            path="/crm/forms/smart-table/:view_id"
                            component={CustomFormsSmartTable}
                          />
                          <AdvisorRoute
                            exact
                            path={`/crm/forms/:household_id(${UUID_ROUTE_SEGMENT})/:submission_id(${UUID_ROUTE_SEGMENT})`}
                            component={ViewFormAnswers}
                          />

                          <AdvisorRoute
                            exact
                            path="/crm/households/:household_id"
                            component={ClientDashboard}
                          />

                          <AdvisorRoute
                            exact
                            path="/crm/resources"
                            component={AdvisorResources}
                          />
                          <AdvisorRoute
                            exact
                            path="/crm/resources/:resource_id"
                            component={AdvisorResourcePage}
                          />
                          <AdvisorRoute
                            exact
                            path="/crm/advice-library"
                            component={AdviceLibraryManagement}
                          />
                          <AdvisorRoute
                            exact
                            path="/crm/advice-library/:topic_id"
                            component={AdviceLibraryTopicDetail}
                          />
                          <AdvisorRoute
                            exact
                            path="/crm/documentation"
                            component={Documentation}
                          />
                          <AdvisorRoute
                            exact
                            path="/crm/documentation/:sectionId"
                            component={Documentation}
                          />
                          <PrivateRoute
                            exact
                            path="/advice-library"
                            component={ClientAdviceLibrary}
                          />
                          <PrivateRoute
                            exact
                            path="/advice-library/:topicId"
                            component={ClientAdviceLibrary}
                          />
                          <AdvisorRoute
                            exact
                            path="/crm/agents"
                            component={SubAgentsManagement}
                          />
                          <AdvisorRoute
                            exact
                            path="/crm/dashboard"
                            component={AdvisorDashboard}
                          />
                          <AdvisorRoute
                            exact
                            path="/crm/pipeline"
                            component={ClientJourney}
                          />
                          <AdvisorRoute
                            exact
                            path="/crm/onboarding/smart-table/:view_id"
                            component={OnboardingDashboard}
                          />
                          <AdvisorRoute
                            exact
                            path="/crm/prospects/smart-table/:view_id"
                            component={ProspectDashboard}
                          />
                          <AdvisorRoute
                            exact
                            path="/crm/meetings/smart-table/:view_id"
                            component={MeetingDashboard}
                          />

                        
                          {/* End Advisor ------------------------------------------------------------------- End Advisor */}{" "}
                          <AdvisorRoute
                            exact
                            path="/crm/reminders"
                            component={RemindersDashboard}
                          />
                          <AdvisorRoute
                            exact
                            path="/crm/notifications/smart-table/:view_id"
                            component={NotificationsSmartTable}
                          />

                          <AdminRoute
                            exact
                            path="/crm/analytics"
                            component={AnalyticsModule}
                          />
                          {/* End Advisor ------------------------------------------------------------------- End Advisor */}{" "}
                          {/* Admin ------------------------------------------------------------------- Admin */}{" "}
                          <AdminRoute
                            exact
                            path="/admin/dashboard"
                            component={AdminDashboard}
                          />
                          <SuperAdminRoute
                            exact
                            path="/admin/portal"
                            component={SuperAdminPortal}
                          />
                          <SuperAdminRoute
                            exact
                            path="/crm/super-admin/dev-dashboard"
                            component={DevDashboard}
                          />
                          <SuperAdminRoute
                            exact
                            path="/crm/super-admin/reliability"
                            component={ReliabilityDashboard}
                          />
                          <SuperAdminRoute
                            exact
                            path="/crm/super-admin/security"
                            component={SecurityCenter}
                          />
                          <SuperAdminRoute
                            exact
                            path="/crm/super-admin/dev-tools"
                            component={DevToolsIndex}
                          />
                          <SuperAdminRoute
                            exact
                            path="/crm/super-admin/dev-tools/plaid"
                            component={PlaidInspector}
                          />
                          <SuperAdminRoute
                            exact
                            path="/crm/super-admin/activity-dashboard"
                            component={ActivityDashboard}
                          />
                          <SuperAdminRoute
                            exact
                            path="/crm/super-admin/activity-dashboard/users/:user_id?"
                            component={UserTimelinePage}
                          />
                          <SuperAdminRoute
                            exact
                            path="/crm/super-admin/activity-dashboard/flows"
                            component={UserFlowPage}
                          />
                          <SuperAdminRoute
                            exact
                            path="/crm/super-admin/activity-dashboard/retention"
                            component={RetentionPage}
                          />
                          <SuperAdminRoute
                            exact
                            path="/crm/super-admin/parabot-feedback"
                            component={ParabotFeedback}
                          />
                          <SuperAdminRoute
                            exact
                            path="/crm/super-admin/parabot-traces/:trace_id"
                            component={ParabotTraceDetail}
                          />
                          <SuperAdminRoute
                            exact
                            path="/crm/super-admin/parabot-traces"
                            component={ParabotTraces}
                          />
                          <SuperAdminRoute
                            exact
                            path="/crm/dev-tickets/smart-table/:view_id"
                            component={DevTicketsSmartTable}
                          />
                          <SuperAdminRoute
                            exact
                            path="/crm/pentester/smart-table/:view_id"
                            component={PentesterAttemptsSmartTable}
                          />
                          {/* End Admin ------------------------------------------------------------------- End Admin */}
                          {/* Content ------------------------------------------------------------------- End Content */}
                          {/* Blog Content Manager ---------------------------------------------------------------------- Blog Content Manager */}
                          <AuthorizedRoute
                            exact
                            path="/content/blog/dashboard"
                            component={BlogDashboard}
                            authorizationType={AUTHORIZATION.BLOG_EDITOR}
                          />
                          <AuthorizedRoute
                            exact
                            path="/content/blog/edit/:blog_post_id"
                            component={EditBlog}
                            authorizationType={AUTHORIZATION.BLOG_EDITOR}
                          />
                          {/* End Blog Content Manager ---------------------------------------------------------End Blog Content Manager */}
                          {/* Learning Lab Content Manager ---------------------------------------------------------Learning Lab Content Manager */}{" "}
                          <AdvisorRoute
                            exact
                            path="/content/learning_lab/dashboard"
                            component={EditLearningLabDashboard}
                          />
                          <AdvisorRoute
                            exact
                            path="/content/learning_lab/edit/:content_id"
                            component={EditContent}
                          />
                          {/* End Learning Lab Content Manager ---------------------------------------------------------End Learning Lab Content Manager */}
                          {/* End Content -------------------------------------------------------------------- End Content */}
                          {/* Old Dead Links */}
                          <Route
                            exact
                            path="/post/what-is-esg-investing-any-why-is-it-becoming-such-a-huge-trend"
                            component={BlogDashboard}
                          />{" "}
                          <Route
                            exact
                            path="/post/activeversuspassive"
                            component={BlogDashboard}
                          />

                          <Route component={NotFound} />
                        </Switch>
                      </Suspense>{" "}
                      </PageTrackingProvider>
                    </Router>}</NavigationPrompt.Provider>
                  </div>
                  </DraftProvider>
                  </AuthCheckProvider>
                </ParabotProvider>
              </AppProvider>
            </AuthProvider>
            </FirmConfigProvider>{" "}
            <ReactQueryDevtools />
          </QueryClientProvider>
        </ErrorBoundary>
      </CustomErrorBoundary>
    </div>
  );
};

export default App;
